// src/pages/CreatorStudio.jsx

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import API from "../services/api";

const CreatorStudio = () => {
  const navigate = useNavigate();

  const [assets, setAssets] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [creator, setCreator] = useState(null);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] =
    useState(false);

  const [title, setTitle] = useState("");
  const [description, setDescription] =
    useState("");
  const [platform, setPlatform] =
    useState("tiktok");
  const [contentLink, setContentLink] =
    useState("");

  // =========================
  // FETCH EVERYTHING
  // =========================

  useEffect(() => {
    const load = async () => {
      try {
        const token =
          localStorage.getItem("token");

        const [
          assetsRes,
          analyticsRes,
          creatorRes,
        ] = await Promise.all([
          API.get("/support-assets/me", {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }),

          API.get("/creator-profile/analytics", {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }),

          API.get("/creator-profile/me", {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }),
        ]);

        setAssets(assetsRes.data);
        setAnalytics(analyticsRes.data);
        setCreator(creatorRes.data);
      } catch (err) {
        console.error("Load error:", err);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  // =========================
  // CREATE CONTENT
  // =========================

  const handleUpload = async () => {
    if (submitting) return;

    if (
      !title ||
      !description ||
      !contentLink
    ) {
      alert("Fill all fields");
      return;
    }

    // ✅ LIMIT TO ONE PAGE
    if (assets.length >= 1) {
      alert(
        "You can only create one creator page"
      );
      return;
    }

    try {
      setSubmitting(true);

      const token =
        localStorage.getItem("token");

      await API.post(
        "/support-assets",
        {
          title,
          description,
          platform,
          content_link: contentLink,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // REFRESH
      const res = await API.get(
        "/support-assets/me",
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setAssets(res.data);

      setTitle("");
      setDescription("");
      setContentLink("");

      alert(
        "🔥 Creator page created successfully!"
      );
    } catch (err) {
      console.error(
        "Upload error:",
        err
      );

      alert("Upload failed");
    } finally {
      setSubmitting(false);
    }
  };

  // =========================
  // WATCH CONTENT
  // =========================

  const watchVideo = (asset) => {
    if (!asset?.content_link) return;

    window.open(
      asset.content_link,
      "_blank"
    );

    // optimistic update
    setAssets((prev) =>
      prev.map((a) =>
        a.id === asset.id
          ? {
              ...a,
              clicks: (a.clicks || 0) + 1,
            }
          : a
      )
    );

    fetch(
      `https://creator-mvp-hvdx.onrender.com/support-assets/click/${asset.slug}`,
      {
        method: "POST",
      }
    ).catch(() => {});
  };

  // =========================
  // PROFILE SETTINGS
  // =========================

  const goToProfileSettings = () => {
    navigate("/profile/settings");
  };

  // =========================
  // TOGGLE VISIBILITY
  // =========================

  const toggleVisibility = async (
    asset
  ) => {
    try {
      const token =
        localStorage.getItem("token");

      const endpoint = asset.is_public
        ? `/support-assets/${asset.id}/unlist`
        : `/support-assets/${asset.id}/relist`;

      const res = await API.patch(
        endpoint,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const updatedAsset = res.data;

      setAssets((prev) =>
        prev.map((a) =>
          a.id === asset.id
            ? updatedAsset
            : a
        )
      );
    } catch (err) {
      console.error(err);
      alert("Failed to toggle visibility");
    }
  };

  if (loading) {
    return <h2>Loading... ⏳</h2>;
  }

  return (
    <div className="container">
      <h1> Creator Studio</h1>

      {/* ========================= */}
      {/* EARNINGS */}
      {/* ========================= */}

      <div className="card">
        <h3>💰 Earnings</h3>

        <p>
          💰 Available:
          ₦
          {analytics?.available_balance ||
            0}
        </p>

        <p>
          🔒 Pending:
          ₦
          {analytics?.pending || 0}
        </p>

        <p>
          📤 Withdrawn:
          ₦
          {analytics?.withdrawn || 0}
        </p>

        <p>
          🔥
          {" "}
          {analytics?.support_count ||
            0}
          {" "}
          supporters
        </p>

        <p>
          💳 Gross Support:
          ₦
          {analytics?.gross_support ||
            0}
        </p>

        <p>
          ⚙️ Platform Fees (10%):
          ₦
          {analytics?.total_fees || 0}
        </p>

        {/* TRANSPARENCY */}

        <div
          style={{
            marginTop: 15,
            padding: 12,
            borderRadius: 12,
            background: "#f8fafc",
            border:
              "1px solid #e2e8f0",
          }}
        >
          <p
            style={{
              margin: 0,
              fontSize: 14,
              fontWeight: "bold",
            }}
          >
            🔒 Transparent Creator
            Payouts
          </p>

          <p
            style={{
              marginTop: 8,
              fontSize: 12,
              opacity: 0.7,
              lineHeight: 1.5,
            }}
          >
            Creator Yard deducts a
            secure payment processing
            fee of approximately 10%
            from successful support
            payments before payout.

            <br />
            <br />

            Withdrawals are processed
            manually within 24-48
            hours.
          </p>
        </div>

        <button
          className="button"
          onClick={() =>
            navigate("/withdraw")
          }
          style={{ marginTop: 15 }}
        >
          Withdraw
        </button>
      </div>

      {/* ========================= */}
      {/* CREATE CONTENT */}
      {/* ========================= */}

      <div
        className="card"
        style={{ marginTop: 30 }}
      >
        <h3>Create Content</h3>

        <input
          className="input"
          placeholder="Creator headline"
          value={title}
          onChange={(e) =>
            setTitle(e.target.value)
          }
        />

        <textarea
          className="textarea"
          placeholder="Tell fans why they should support you"
          value={description}
          onChange={(e) =>
            setDescription(
              e.target.value
            )
          }
        />

        <select
          className="select"
          value={platform}
          onChange={(e) =>
            setPlatform(e.target.value)
          }
        >
          <option value="tiktok">
            TikTok
          </option>

          <option value="instagram">
            Instagram
          </option>

          <option value="x">
            X
          </option>

          <option value="facebook">
            Facebook
          </option>
        </select>

        <input
          className="input"
          placeholder="Profile link"
          value={contentLink}
          onChange={(e) =>
            setContentLink(
              e.target.value
            )
          }
        />

        <button
          className="button"
          onClick={handleUpload}
          disabled={submitting}
        >
          {submitting
            ? "Processing..."
            : assets.length >= 1
            ? "Creator Page Already Exists"
            : "Launch Creator Page"}
        </button>
      </div>

      {/* ========================= */}
      {/* CONTENT */}
      {/* ========================= */}

      <div style={{ marginTop: 30 }}>
        <h3>Your Content</h3>

        {assets.length === 0 && (
          <div className="card">
            <p>
              No content yet —
              create your first
              creator page 🚀
            </p>
          </div>
        )}

        {assets.map((a) => (
          <div
            key={a.id}
            className="card"
            style={{
              marginBottom: 20,
            }}
          >
            {/* CREATOR */}

            <div
              onClick={
                goToProfileSettings
              }
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                marginBottom: 14,
                cursor: "pointer",
              }}
            >
              <img
                src={
                  creator?.profile_image ||
                  "https://via.placeholder.com/55"
                }
                alt="creator"
                style={{
                  width: 55,
                  height: 55,
                  borderRadius: "50%",
                  objectFit: "cover",
                  border:
                    "2px solid #22c55e",
                }}
              />

              <div>
                <h4
                  style={{
                    margin: 0,
                  }}
                >
                  {creator?.display_name ||
                    "Creator"}
                </h4>

                <p
                  style={{
                    margin: 0,
                    fontSize: 12,
                    opacity: 0.7,
                  }}
                >
                  Contest Participant
                </p>
              </div>
            </div>

            {/* CONTENT INFO */}

            <h3>{a.title}</h3>

            <p>{a.description}</p>

            <p
              style={{
                fontSize: 12,
                opacity: 0.7,
                marginTop: 6,
              }}
            >
              🔗 {a.content_link}
            </p>

            <p
              style={{
                fontSize: 12,
                opacity: 0.7,
              }}
            >
              🏷 Slug: {a.slug}
            </p>

            <p
              style={{
                fontSize: 12,
                opacity: 0.7,
              }}
            >
              📅 Created:
              {" "}
              {a.created_at
                ? new Date(
                    a.created_at
                  ).toLocaleString()
                : "N/A"}
            </p>

            <p
              style={{
                fontSize: 12,
                opacity: 0.7,
              }}
            >
              ❤️ Last Support:
              {" "}
              {a.last_supported_at
                ? new Date(
                    a.last_supported_at
                  ).toLocaleString()
                : "No support yet"}
            </p>

            {/* ANALYTICS */}

            <div
              style={{
                background:
                  "#f8fafc",
                padding: 14,
                borderRadius: 14,
                marginTop: 10,
                marginBottom: 10,
                border:
                  "1px solid #e2e8f0",
              }}
            >
              <p>
                👁 Views:
                {" "}
                {a.clicks || 0}
              </p>

              <p>
                🔥 Supporters:
                {" "}
                {a.support_count ||
                  0}
              </p>

              <p>
                💳 Gross Support:
                ₦
                {a.total_gross || 0}
              </p>

              <p>
                ⚙️ Processing Fees
                (2.5%):
                ₦
                {a.total_fees || 0}
              </p>

              <p
                style={{
                  fontWeight:
                    "bold",
                  color: "#16a34a",
                  fontSize: 16,
                }}
              >
                💰 Net Earnings:
                ₦
                {a.total_support ||
                  0}
              </p>
            </div>

            {/* STATUS */}

            <div
              style={{
                display: "flex",
                gap: 10,
                flexWrap: "wrap",
                marginTop: 10,
                marginBottom: 10,
              }}
            >
              <span
                style={{
                  background:
                    a.is_public
                      ? "#dcfce7"
                      : "#fee2e2",

                  color: a.is_public
                    ? "#166534"
                    : "#991b1b",

                  padding: "6px 10px",
                  borderRadius: 999,
                  fontSize: 12,
                  fontWeight: "bold",
                }}
              >
                {a.is_public
                  ? "🌍 PUBLIC"
                  : "🙈 HIDDEN"}
              </span>

              <span
                style={{
                  background:
                    "#eff6ff",
                  color: "#1d4ed8",
                  padding: "6px 10px",
                  borderRadius: 999,
                  fontSize: 12,
                  fontWeight: "bold",
                }}
              >
                {a.platform?.toUpperCase()}
              </span>
            </div>

            {/* ACTIONS */}

            <div
              className="row"
              style={{
                marginTop: 15,
                display: "flex",
                gap: 10,
                flexWrap: "wrap",
              }}
            >
              <button
                className="button-secondary"
                onClick={() =>
                  watchVideo(a)
                }
              >
                ▶ Watch
              </button>

              <button
                className="button"
                onClick={() =>
                  navigate(
                    `/support/${a.slug}/custom`
                  )
                }
              >
                💖 Support Page
              </button>

              <button
                className="button-secondary"
                onClick={() =>
                  window.open(
                    `https://creator-yard.com/creator/${creator?.display_name}`,
                    "_blank"
                  )
                }
              >
                👁 Preview Profile
              </button>

              <button
                className="button-danger"
                onClick={() =>
                  toggleVisibility(a)
                }
              >
                {a.is_public
                  ? "Unlist"
                  : "Make Public"}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CreatorStudio;